import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { tap, switchMap, finalize } from 'rxjs/operators';
import { Parameter, Plan, PreRequisiteStepError, PREREQ_PARAM_ERROR_TYPE, Step, Variable, ExecutePlanRequest } from 'src/app/model/Plan';
import { EventService } from 'src/app/service/event.service';
import { PlanCallbackService } from 'src/app/service/plan-callback.service';
import { PlanService } from 'src/app/service/plan.service';
import { Events } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { ColumnType, DataTableColumn, DataTableRow } from '../controls/datatable/datatable.component';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { WizardStep } from '../controls/wizard/wizard.component';

enum PlansExecutionState {
  VALIDATE = "VALIDATE",
  REVIEW = "REVIEW",
  EXECUTE = "EXECUTE"
}

@Component({
    selector: 'app-plans-execution',
    templateUrl: './plans-execution.component.html',
    styleUrls: ['./plans-execution.component.css'],
    standalone: false
})
export class PlansExecutionComponent implements OnInit {

  stepIndex:number = 0;
  steps:WizardStep[] = [];
  state:PlansExecutionState = PlansExecutionState.VALIDATE;
  PlansExecutionState = PlansExecutionState;
  planId: number;
  plan: Plan = new Plan();
  preReqErrors:PreRequisiteStepError[] = [];
  isValidating:boolean = true;
  nextButton:string = $localize`Next`;
  nextButtonId:string = "";
  prevButtonId:string = "";

  // validation state
  cols: DataTableColumn[] = [];
  rows: DataTableRow[] = [];
  validationErrorTableTitle: string = $localize`Validation errors`;

  // review state
  stepCols: DataTableColumn[] = [];
  stepRows: DataTableRow[] = [];
  reviewTableTitle: string = $localize`Parameters`;
  togglePwd:boolean = false;
  isReviewComplete:boolean = false;

  // stepsWithSensitiveParam:Step[] = [];
  isStepTableLoading:boolean = false;

  constructor(private route: ActivatedRoute, private planSrv: PlanService, private eventService: EventService, private modal: Modal,
    private router: Router) { }

  ngOnInit(): void {
    this.planId = +this.route.snapshot.paramMap.get('planId');

    this.cols = Array<DataTableColumn>( new DataTableColumn("num", $localize`No.`, ColumnType.STRING, false, "5"),
                                        new DataTableColumn("nickName", $localize`Name`, ColumnType.STRING, false, "35"),
                                        new DataTableColumn("error", $localize`Error`, ColumnType.STRING, false, "60")
                                      );
    this.stepCols = Array<DataTableColumn>( new DataTableColumn("no", $localize`No.`, ColumnType.STRING, false, "5"),
                                            new DataTableColumn("name", $localize`Name`, ColumnType.STRING, false, "35"),
                                            new DataTableColumn("group", $localize`Group`, ColumnType.STRING, false, "10"),
                                            new DataTableColumn("paramName", $localize`Parameter`, ColumnType.STRING, false, "15"),
                                            new DataTableColumn("paramHelp", $localize`Description`, ColumnType.STRING, false, "20"),
                                            new DataTableColumn("paramValue", $localize`Value`, ColumnType.PASSWORD, false, "15"),
                                          );

    if(this.planId > 0){
      this.steps = [{id: PlansExecutionState.VALIDATE, 'name': $localize`Validate`},
                  {id: PlansExecutionState.REVIEW, 'name': $localize`Review`},
                  {id: PlansExecutionState.EXECUTE, 'name': $localize`Execute`}];
      this.init();
    }else{
      this.router.navigate(['/plans/overview']);
    }
  }

  goToReview():void {
    this.setState(PlansExecutionState.REVIEW);
    this.isReviewComplete = false;
    this.isStepTableLoading = true;

    if(this.stepRows.length == 0){
      this.togglePwd = false;
      //check for sensivitive parmaeters
      this.plan.steps.forEach((step, index) => {
        let stepToReview:Step = new Step();
        stepToReview.nickname = step.nickname;
        stepToReview.stepNo = index;
        stepToReview.label = step.label;
        stepToReview.parameters = {};
        let parameters:Parameter[] = Object.values(step.parameters);

        parameters.forEach((param) => {
          if(!step.disabled && param.sensitive){
            let isDependencyValidated:boolean = true;

            if(param.dependencies){
              for(let dep of param.dependencies){
                let p = step.parameters[dep.name];
    
                //check for callback
                if(dep.callback && typeof PlanCallbackService[dep.callback] === "function"){
                  isDependencyValidated = PlanCallbackService[dep.callback](step.parameters, dep, dep.callbackValue);
                  if(!isDependencyValidated){
                    break;
                  }
                }else{
                  if(dep.name === p.name && p.value !== dep.value){
                    isDependencyValidated = false;
                    break;
                  }
                }
              }
            }
            
            if(isDependencyValidated){
              stepToReview.parameters[param.name] = param;
            }
          }
        });

        Object.values(stepToReview.parameters).sort((a, b) => {
          return a.group.localeCompare(b.group) || a.displayName.localeCompare(b.displayName);
        }).forEach( param => {
          let paramRow:any = {param: param,
            paramValue: {value: '', showPassword: false, altText: param.displayName},
            paramName: param.displayName,
            paramHelp: param.description,
            no: stepToReview.stepNo + 1,
            name: stepToReview.label && stepToReview.label !== '' ? `${stepToReview.nickname} (${stepToReview.label})` : stepToReview.nickname, 
            group: param.group === "" ? $localize`Default` : param.group,
            step: stepToReview,
            isVariable: false
          };
          this.stepRows.push(new DataTableRow(paramRow, 
                                              [],
                                              param.required ? true : false,
                                              false,
                                              false,
                                              param.required ? $localize`Required` : '',
                                              ));
        });
      });

      this.plan.variables.forEach((variable, index) => {
        if (variable.sensitive && !this.isVariableDisabled(variable, this.plan.steps)) {
          let paramRow: any = {
            param: variable,
            paramValue: { value: '', showPassword: false, altText: '' },
            paramName: variable.name,
            paramHelp: '',
            no: index + 1,
            name: $localize`Plan variable`,
            group: $localize`Variable`,
            step: variable,
            isVariable: true
          };

          this.stepRows.push(new DataTableRow(paramRow,
            [],
            true,
            false,
            false,
            $localize`Required`
          ));
        }
      });
    }else{
      for(var i = 0; i < this.stepRows.length; i++) {
        this.onParamValueChange(this.stepRows[i]);
        if(!this.isReviewComplete){
          break;
        }
      }
    }

    this.isStepTableLoading = false;

    //nothing to review
    if(this.stepRows.length == 0){
      this.goToExecute();
    }
  }

  isVariableDisabled(variable: Variable, steps: Step[]): boolean {
    //a variable can be used in multiple steps and properties so 
    //it must be disabled in all 
    let disabled: boolean = true;
    for(let i = 0; i < steps.length; i++){
      let step = steps[i];
      if( !step.disabled && step.variableMap[variable.name] != undefined){
        disabled = false;
        break;
      }
    }
    return disabled;
  }

  onParamValueChange(row:DataTableRow): void {
    let isReadyToExecute:boolean = true;

    for(let i = 0; i < this.stepRows.length; i++){
      if(this.stepRows[i].data.param.required && this.stepRows[i].data.paramValue.value == ''){
        isReadyToExecute = false;
        break;
      }
    }

    row.error = row.data.param.required && row.data.paramValue.value == '';

    this.isReviewComplete = isReadyToExecute;
  }

  onTogglePwdCheckbox(): void {
    this.stepRows.forEach( row => {
      row.data.paramValue.showPassword = this.togglePwd;
    });
  }

  goToPlanDetails():void {
    this.router.navigate([`/plans/details/${this.planId}`]);
  }

  goToPlanOverview():void {
    this.router.navigate(['/plans/overview']);
  }

  goToNextState(): void {
    switch (this.state) {
      case PlansExecutionState.VALIDATE:
        this.goToReview();
        break;
      case PlansExecutionState.REVIEW:
          this.goToExecute();
          break;
      case PlansExecutionState.EXECUTE:
        this.execute();
        break;
    }
  }

  goToPrevState(): void {
    switch (this.state) {
      case PlansExecutionState.REVIEW:
        this.goToValidate();
        break;
      case PlansExecutionState.EXECUTE:
        if(this.stepRows.length == 0){
          this.goToValidate();
        }else{
          this.goToReview();
        }
        break;
    }
  }

  isDisableNextBtn(): boolean {
    let isDisabled:boolean = false;

    switch (this.state) {
      case PlansExecutionState.VALIDATE:
        isDisabled = this.preReqErrors.length > 0 || this.isValidating;
        break;
      case PlansExecutionState.REVIEW:
        isDisabled = !this.isReviewComplete;
        break;
      case PlansExecutionState.EXECUTE:
        isDisabled = this.preReqErrors.length > 0;
        break;
    }

    return isDisabled;
  }

  setState(newState:PlansExecutionState): void{
    this.state = newState;
    this.nextButton = this.state == PlansExecutionState.EXECUTE ? $localize`Submit` : $localize`Next`;
    let index:number = this.steps.findIndex( s => {
      return s.id == this.state;
    });

    switch (this.state) {
      case PlansExecutionState.VALIDATE:
        this.nextButtonId = "otsc-plan-execution-next-btn-val-review";
        this.prevButtonId = undefined;
        break;
      case PlansExecutionState.REVIEW:
        this.nextButtonId = "otsc-plan-execution-next-btn-review-exec";
        this.prevButtonId = "otsc-plan-execution-prev-btn-review-val";
        break;
      case PlansExecutionState.EXECUTE:
        this.nextButtonId = "otsc-plan-execution-execute-btn";
        this.prevButtonId = this.stepRows.length == 0 ? "otsc-plan-execution-prev-btn-exec-val" : "otsc-plan-execution-prev-btn-exec-review";
        break;
    }
    this.stepIndex = index > -1 ? index : 0;
  }

  goToValidate():void {
    this.init();
    this.setState(PlansExecutionState.VALIDATE);
  }

  goToExecute(): void {
    this.setState(PlansExecutionState.EXECUTE);
  }

  execute(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Execute`,
          message: $localize`<b>${this.plan.name}</b> is ready to be executed. Execute selected plan?`,
          primaryBtn: $localize`Execute`,
          secondaryBtn: $localize`Cancel`,
          type: ModalMessageType.Question,
          width: '350px',
          disableButtonsOnClick: true
        },
        id: 'otsc-plans-execute-msg'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          let sensitiveParamMap: Record<number, Step> = {};
          let sensitiveVariableMap: Record<string, Variable> = {};

          this.stepRows.forEach(row => {
            row.data.param.value = row.data.paramValue.value; //set value
            
            if(row.data.isVariable) {
              sensitiveVariableMap[row.data.param.name] = row.data.param;
            } else {
              if (sensitiveParamMap[row.data.no] === undefined) {
                sensitiveParamMap[row.data.no] = row.data.step;
              }
              sensitiveParamMap[row.data.no].parameters[row.data.param.name] = row.data.param;
            }
          });

          this.planSrv.executePlan(this.planId, new ExecutePlanRequest( Object.values(sensitiveParamMap), sensitiveVariableMap)).pipe(
            tap(resp => {
              Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`${this.plan.name} plan was initiated.`);
              this.router.navigate(['/activities/executionplans']);
            })
          ).subscribe();
        }
        sub.unsubscribe();
      });
  }

  cancelPlansExecutionWizard(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Cancel plan execution`,
          message: $localize`Do you want to cancel the plan execution?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px'
        },
        id: 'otsc-plans-execution-cancel-msg'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.goToPlanOverview();
        }
        sub.unsubscribe();
      });
  }

  private init():void {
    this.setState(PlansExecutionState.VALIDATE);
    this.isValidating = true;
    this.rows = [];

    this.planSrv.getPlan(this.planId).pipe(
      tap(resp => {
        this.plan = resp.body;
      }),
      switchMap(()=> this.planSrv.runPrerequisiteChecks(this.planId, this.plan.steps).pipe(
        tap(resp => {
          this.preReqErrors = resp.body;
          this.preReqErrors.forEach((error) => {
            if(!error.isAgentOnline && error.isAgentRegistered && error.isAgentEnvironment && error.isAgentActive){
              this.rows.push(new DataTableRow({num: error.num, nickName: error.nickName, error: $localize`The selected agent is currently offline.`}, []));
            }else if(error.isAgentRegistered && !error.isAgentEnvironment){
              this.rows.push(new DataTableRow({num: error.num, nickName: error.nickName, error: $localize`The selected agent is not authorized within the current System Center environment.`}, []));
            }else if(!error.isAgentRegistered){
              this.rows.push(new DataTableRow({num: error.num, nickName: error.nickName, error: $localize`The selected agent is not currently registered with System Center.`}, []));
            }else if(error.isAgentRegistered && !error.isAgentActive){
              this.rows.push(new DataTableRow({num: error.num, nickName: error.nickName, error: $localize`The selected agent is currently disabled.`}, []));
            }

            error.parameters.forEach((paramError) => {
              if(paramError.type == PREREQ_PARAM_ERROR_TYPE.EXT_FILE){
                this.rows.push(new DataTableRow({num: error.num, nickName: error.nickName, 
                  error: $localize`The selected external file ${paramError.parameter.value} for the parameter '${paramError.parameter.displayName}' in '${paramError.parameter.group === "" ? 'Default' : paramError.parameter.group}' could not be found.`}, [])); 
              }else if(paramError.type == PREREQ_PARAM_ERROR_TYPE.PRODUCT_FILE){
                this.rows.push(new DataTableRow({num: error.num, nickName: error.nickName, 
                  error: $localize`The selected software file ${paramError.parameter.value} for the parameter '${paramError.parameter.displayName}' in '${paramError.parameter.group === "" ? 'Default' : paramError.parameter.group}' could not be found.`}, [])); 
              }
            });
          });
        })
      )),
      finalize(()=>{
        this.isValidating = false;
      })
    ).subscribe();
  }
}
